import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  User as UserIcon,
  Upload,
  FileText,
  Users,
  Trophy,
  BarChart3,
  Menu,
  X,
  Brain,
  Briefcase,
  CheckCircle,
  XCircle,
  BookOpen,
  Crown,
  Database,
  HelpCircle,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext.jsx";
import { firebaseApp } from "../../firebase";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";

const cn = (
  ...classes
) => classes.filter(Boolean).join(" ");

const Sidebar = ({ userType, onOpenTicketModal }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const hoverTimeoutRef = React.useRef(null);
  const db = getFirestore(firebaseApp);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [skillsVerified, setSkillsVerified] = useState(false);

  useEffect(() => {
    if (userType !== "jobseeker" || !user?.id) return;

    const profRef = doc(db, "userProfiles", user.id);
    const jsRef = doc(db, "jobseekers", user.id);

    const unsubProfile = onSnapshot(profRef, async (profSnap) => {
      let uploaded = false;
      let verified = false;

      if (profSnap.exists()) {
        const data = profSnap.data();
        uploaded = !!data?.resumeUploaded;
        verified = !!data?.skillsVerified;
      }

      // If not found in userProfiles, check jobseekers as fallback
      if (!uploaded) {
        try {
          const jsSnap = await getDoc(jsRef);
          if (jsSnap.exists()) {
            uploaded = !!jsSnap.data()?.resumeUploaded;
          }
        } catch (err) {
          console.warn("Failed to check jobseekers fallback:", err);
        }
      }

      setResumeUploaded(uploaded);
      setSkillsVerified(verified);
    }, (err) => {
      console.error("Profile subscription error:", err);
      setResumeUploaded(false);
      setSkillsVerified(false);
    });

    return () => {
      unsubProfile();
    };
  }, [db, userType, user?.id]);

  const shouldBeExpanded = isExpanded || isHovering;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 100);
  };

  const navigationItems = useMemo(() => {
    switch (userType) {
      case "jobseeker":
        const today = new Date();
        const isFifthOfMonth = today.getDate() === 5;

        // Show resume upload button if: not uploaded yet OR (uploaded AND it's the 5th)
        const shouldShowResumeUpload = !resumeUploaded || (resumeUploaded && isFifthOfMonth);

        const base = [
          { path: "/job-seeker/profile", icon: Home, label: "Dashboard", color: "text-blue-600" },
          ...(shouldShowResumeUpload ? [{ path: "/job-seeker/resume-upload", icon: Upload, label: "Resume Upload", color: "text-purple-600" }] : []),
          { path: "/job-seeker/tests", icon: Briefcase, label: "Job Searching", color: "text-green-600" },
          { path: "/job-seeker/my-courses", icon: BookOpen, label: "Personalized Path", color: "text-orange-600" },
          { path: "/job-seeker/results", icon: Trophy, label: "Results", color: "text-amber-600" },
          { path: "/job-seeker/subscription", icon: Crown, label: "Go Premium", badge: "Pro", color: "text-pink-600" },
        ];
        // Only restrict nav if resume is NOT uploaded AND user is not on results/post-test pages
        const isOnResultsOrPostTest = location.pathname.includes('/results') || location.pathname.includes('/skills-test-results');
        if (!resumeUploaded && !isOnResultsOrPostTest) {
          return [{ path: "/job-seeker/resume-upload", icon: Upload, label: "Resume Upload" }];
        }
        return base;
      case "hr":
        return [
          { path: "/hr", icon: Home, label: "Dashboard", color: "text-red-600" },
          { path: "/hr/jd-upload", icon: Upload, label: "Upload JD", color: "text-blue-600" },
          { path: "/hr/my-jobs", icon: Briefcase, label: "Jobs", color: "text-orange-600" },
          { path: "/hr/test-generator", icon: Brain, label: "Test Generator", badge: "New", color: "text-purple-600" },
          { path: "/hr/my-tests", icon: FileText, label: "Tests Library", color: "text-green-600" },
          { path: "/hr/candidates", icon: Users, label: "Candidates", color: "text-pink-600" },
        ];
      case "admin":
        return [
          { path: "/admin", icon: Home, label: "Dashboard" },
          { path: "/admin/users", icon: Users, label: "User Management" },
          { path: "/admin/badges", icon: Trophy, label: "Badge Management" },
          { path: "/admin/migrate-tests", icon: Database, label: "Migrate Tests", badge: "New" },
          { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
        ];
      default:
        return [];
    }
  }, [userType, resumeUploaded, location.pathname]);

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "fixed left-0 top-0 h-screen flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out shadow-sm z-40",
        shouldBeExpanded ? "w-64" : "w-16"
      )}
    >
      {/* Header with Logo */}
      <div className={cn(
        "flex items-center py-5 border-b border-gray-200 justify-center",
        shouldBeExpanded ? "px-4" : "px-2"
      )}>
        <Link
          to="/"
          aria-label="VetriaAI"
          className="flex items-center -space-x-[5px] transition-all duration-300"
        >
          <img
            src="/VetriaAI.png"
            alt="VetriaAI"
            className="h-9 w-9"
          />
          {shouldBeExpanded && (
            <span className="text-xl font-semibold tracking-tight text-gray-900">
              VetriaAI
            </span>
          )}
        </Link>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 space-y-1 px-3 py-5 overflow-y-auto overflow-x-hidden">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              title={!shouldBeExpanded ? item.label : undefined}
              className={cn(
                "group relative flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium transition-all duration-200",
                isActive
                  ? "bg-red-50 text-red-600"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <Icon
                className={cn("h-5 w-5 flex-none", item.color || "text-gray-600")}
                strokeWidth={2}
              />
              {shouldBeExpanded && (
                <div className="flex w-full items-center justify-between gap-2 overflow-hidden">
                  <span className="truncate">{item.label}</span>
                  {item.badge && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-red-600 text-white whitespace-nowrap">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 p-3 space-y-1 bg-gray-50">
        <Link
          to={userType === "jobseeker" ? "/job-seeker/my-profile" : userType === "hr" ? "/hr/my-profile" : "/admin"}
          title={!shouldBeExpanded ? "Profile" : undefined}
          className={cn(
            "group relative flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 w-full"
          )}
        >
          <UserIcon className="h-5 w-5 flex-none text-cyan-600" strokeWidth={2} />
          {shouldBeExpanded && <span>Profile</span>}
        </Link>
        <Link
          to={userType === "jobseeker" ? "/job-seeker/support" : userType === "hr" ? "/hr/support" : "/admin/support"}
          title={!shouldBeExpanded ? "Support" : undefined}
          className={cn(
            "group relative flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 w-full"
          )}
        >
          <HelpCircle className="h-5 w-5 flex-none text-indigo-600" strokeWidth={2} />
          {shouldBeExpanded && <span>Support</span>}
        </Link>
        <button
          onClick={logout}
          title={!shouldBeExpanded ? "Logout" : undefined}
          className={cn(
            "group relative flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200 w-full"
          )}
        >
          <LogOut className="h-5 w-5 flex-none text-red-600" strokeWidth={2} />
          {shouldBeExpanded && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;

